import axios, { AxiosError } from 'axios';
import JSONbig from 'json-bigint';

import {
  DEFAULT_ERROR_MESSAGE,
  ERR_NETWORK_CODE,
  ERROR,
  WARNING,
} from '@app/constants';
import {
  authenticateByCookie,
  extractHeadersForLogs,
  setAxiosConfig,
} from '@app/services';
import { serverStatus } from '@app/services/__global__/serverStatus';
import type { NServices } from '@app/types/Services';
import { hasToken, logOutUser } from '@app/utils';

import type { AxiosResponse } from 'axios';

const handleNativeError = (error: unknown): never => {
  if (axios.isAxiosError(error)) {
    const message = error.response?.data?.ErrorMessage || error.message;

    throw new Error(message, { cause: ERROR });
  }

  if (error instanceof Error) {
    if (error.cause === ERROR) {
      throw new Error(JSON.stringify(error), { cause: ERROR });
    }

    if (error.cause === WARNING) {
      throw new Error(error.message, { cause: WARNING });
    }

    throw new Error(error.message, { cause: ERROR });
  }

  throw new Error(DEFAULT_ERROR_MESSAGE, { cause: ERROR });
};

export const createServerRequest: NServices.CreateServerRequest = async (
  axiosConfig,
  logs
) => {
  try {
    if (!hasToken()) {
      await authenticateByCookie();
    }

    setAxiosConfig();

    let response: AxiosResponse;

    if (logs?.isLogged) {
      const { userId, database } = logs;
      const headers = await extractHeadersForLogs({ userId, database });
      response = await axios({
        ...axiosConfig,
        headers: headers || {},
      });
    } else {
      response = await axios(axiosConfig);
    }

    const { JsonData, ErrorMessage } = response.data;
    let parsedJsonData;

    try {
      parsedJsonData = JSONbig.parse(JsonData);
    } catch (error) {
      parsedJsonData = JsonData;
    }

    if (ErrorMessage) {
      throw new Error(ErrorMessage, { cause: WARNING });
    }

    return parsedJsonData;
  } catch (error) {
    if (error instanceof axios.AxiosError) {
      if (error.status === 401) {
        logOutUser();
      }
    }

    if (serverStatus.isDown) {
      return undefined;
    }

    if (error instanceof AxiosError && error.code === ERR_NETWORK_CODE) {
      throw new Error(DEFAULT_ERROR_MESSAGE, { cause: ERROR });
    }

    handleNativeError(error);
  }
};
